import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Card } from './ui'

interface State {
  error?: Error
}

interface Props {
  children: ReactNode
  label: string
  /**
   * How much of the page this boundary stands in for.
   *
   * A `panel` boundary sits inside the layout, so the navigation, the as-at
   * date and the sign-out button are all still on screen behind its message —
   * it can honestly say the rest of the application still works. An
   * `application` boundary is the outermost thing rendered, above the query
   * client, the session and the router, so when it catches, none of that
   * exists. The two cannot share one message without one of them lying.
   */
  scope?: 'panel' | 'application'
}

/**
 * Stops one broken panel taking the whole application with it.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * which is how a chart that cannot measure itself turns into a blank white
 * page — no navigation, no sign-out, nothing to read, and no indication that
 * anything happened. A test caught exactly that, and it would have behaved the
 * same way in a browser that failed to provide something the charts need.
 *
 * There are two of these because one was not enough. The panel boundary is
 * rendered *inside* the providers, so it cannot catch a provider that throws
 * while mounting — and React then unmounted everything, which is the blank
 * page it was added to prevent, arriving by the one route it could not see.
 * The second boundary wraps the providers themselves.
 *
 * Deliberately a class: this is the one thing in React that hooks cannot do.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = {}

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console rather than sent anywhere. This deployment has no
    // error reporting service, and pretending otherwise would be worse than
    // saying so.
    console.error(`Unhandled error in ${this.props.label}`, error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    /*
     * Clearing the error re-renders the same children, which is the right
     * recovery for a panel that failed on data it can fetch again — and the
     * wrong one for a failure above the providers, where the thing that threw
     * will simply throw again. That case needs a new document, so the button
     * reloads rather than retries.
     */
    if (this.props.scope === 'application') {
      return (
        <div className="flex min-h-dvh items-center justify-center p-6">
          <Card className="max-w-md p-6 text-center">
            <p className="text-sm font-semibold text-low">This application could not start</p>
            <p className="mt-1 text-xs text-ink-faint">
              {error.message}. Nothing has been saved or changed. Reloading the page is usually
              enough; the browser console has the details if it is not.
            </p>
            <Button variant="primary" className="mt-4" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </Card>
        </div>
      )
    }

    return (
      <Card className="p-6 text-center">
        <p className="text-sm font-semibold text-low">This part of the page could not be shown</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-ink-faint">
          {error.message}. The rest of the application is unaffected — the navigation above still
          works.
        </p>
        <Button className="mt-4" onClick={() => this.setState({ error: undefined })}>
          Try again
        </Button>
      </Card>
    )
  }
}
